import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const blank = {
  name: '', phone: '', address: '', price: 50,
  frequency: 'fortnightly', day_of_week: 'mon', time_of_day: '09:00', notes: '',
};

export default function CustomerFormDialog({ open, onOpenChange, onSubmit, initial }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...blank, ...initial } : blank);
      setError('');
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    setError('');
    if (!form.name || !form.phone || !form.address) {
      setError('Name, phone and address are required.');
      return;
    }
    setSaving(true);
    try {
      await onSubmit({ ...form, price: parseFloat(form.price) || 0 });
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="customer-form-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input data-testid="cust-name" value={form.name} onChange={set('name')} placeholder="John Smith" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="cust-phone" value={form.phone} onChange={set('phone')} placeholder="+61 4XX XXX XXX" />
          </div>
          <div>
            <Label>Address</Label>
            <Textarea data-testid="cust-address" rows={2} value={form.address} onChange={set('address')} placeholder="12 Smith St, Dandenong VIC 3175" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Price ($)</Label>
              <Input data-testid="cust-price" type="number" step="5" value={form.price} onChange={set('price')} />
            </div>
            <div>
              <Label>Time of day</Label>
              <Input data-testid="cust-time" type="time" value={form.time_of_day} onChange={set('time_of_day')} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="cust-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="monthly">Monthly (4 weeks)</SelectItem>
                  <SelectItem value="one_off">One-off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Day of week</Label>
              <Select value={form.day_of_week} onValueChange={(v) => setForm({ ...form, day_of_week: v })}>
                <SelectTrigger data-testid="cust-dow"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mon">Monday</SelectItem>
                  <SelectItem value="tue">Tuesday</SelectItem>
                  <SelectItem value="wed">Wednesday</SelectItem>
                  <SelectItem value="thu">Thursday</SelectItem>
                  <SelectItem value="fri">Friday</SelectItem>
                  <SelectItem value="sat">Saturday</SelectItem>
                  <SelectItem value="sun">Sunday</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea data-testid="cust-notes" rows={2} value={form.notes || ''} onChange={set('notes')} placeholder="Gate code, dog warning, etc." />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cust-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="cust-save-btn">
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
